import { Injectable, inject } from '@angular/core';
import {
  calculateDistance,
  isBoxInBox,
  calculateBoxIntersection,
} from '@functions';
import { BoxDetection, OverlayColor } from '@models';
import {
  DetectionService,
  LogicService,
  MessageService,
  RenderService,
  SimulationService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { StratafixMetricsService } from './stratafix-metrics.service';

const GLOBAL_CONFIDENCE = 0.2;
const MIN_NUMBER_OF_STITCHES = 3;
const COEFFICIENT_WOUND_WIDTH_TO_HEIGHT = 0.4;

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private detectionService = inject(DetectionService);
  private logicService = inject(LogicService);
  private messageService = inject(MessageService);
  private renderService = inject(RenderService);
  private simulationService = inject(SimulationService);
  private stratafixMetricsService = inject(StratafixMetricsService);

  cycleWorkerId: string = undefined;
  piercingBackStitchWorkerId: string = undefined;

  sutureInTissueOccurencies = 0;
  needleInTissueOccurencies = 0;
  positionReverseStitching = { x: 0, y: 0 };
  isToShowFirstReverseStitchResult = true;
  distanceNeedleTool: number = null;
  originalWound: BoxDetection = null;
  lastDetectedOpenWound: BoxDetection = null;

  checkSample(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const openWound = this.detectionService.getBox(
          'open_wound',
          this.cycleWorkerId,
        );

        this.originalWound = openWound ?? this.originalWound;

        const isWoundOrientationVertical = openWound
          ? COEFFICIENT_WOUND_WIDTH_TO_HEIGHT *
              (openWound.max.y - openWound.min.y) >
            openWound.max.x - openWound.min.x
          : null;

        this.logicService.checkSamplePosition({
          sample: 'open_wound',
          workerId: this.cycleWorkerId,
          confidence: 0.3,
          width: 640,
          height: 400,
          moveX: -100,
          moveY: 100,
          image: 'overlays/stratafix-wound.svg',
          additionalCondition: isWoundOrientationVertical,
          tolerance: { x: 0, y: 20 },
        });
        resolve(this.simulationService.state() === 'simulation');
      } catch (error) {
        reject(error);
      }
    });
  }

  checkInstruments(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.logicService
          .checkInstrumentsPosition([
            {
              id: 'toolLeft',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: -350,
              radius: 90,
              image: 'overlays/instrument-left.svg',
            },
            {
              id: 'toolRight',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: 170,
              radius: 90,
              image: 'overlays/instrument-right.svg',
            },
          ])
          .then((instrumentsConfirmed) => {
            if (!instrumentsConfirmed) {
              this.messageService.showDetectionMessage({
                text: 'Bring your instruments together in the camera view',
                identifier: 'checkInstruments',
              });
            }
            resolve(instrumentsConfirmed);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  fixation(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const [openWound, tail] = this.detectionService.getBoxes(
          ['open_wound', 'tail'],
          this.cycleWorkerId,
        );

        const [needleInTissue, sutureInTissue] = this.detectionService.getBoxes(
          ['needleInTissue', 'sutureInTissue'],
          this.piercingBackStitchWorkerId,
        );

        const distanceTailWoundTop =
          tail && openWound
            ? calculateDistance(
                { x: tail.center.x, y: tail.center.y },
                { x: openWound.center.x, y: openWound.min.y },
              )
            : null;

        const isTailPlaced =
          tail && openWound
            ? distanceTailWoundTop < 150 &&
              tail.center.y < openWound.min.y &&
              tail.confidence > GLOBAL_CONFIDENCE
            : null;

        const tailOccurencies = this.detectionService.getNumberOfBoxOccurencies(
          'tail',
          this.cycleWorkerId,
          20,
        );

        if (!isTailPlaced && openWound) {
          this.originalWound = openWound ?? this.originalWound;
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/tail-stitch.svg',
              x: openWound.min.x + 30,
              y: openWound.min.y - 40,
            },
          });
        }

        this.sutureInTissueOccurencies =
          this.sutureInTissueOccurencies +
          (sutureInTissue?.confidence > GLOBAL_CONFIDENCE ? 1 : 0);

        this.needleInTissueOccurencies =
          this.needleInTissueOccurencies +
          (needleInTissue?.confidence > GLOBAL_CONFIDENCE ? 1 : 0);

        const isTailPlacedAndFixed =
          isTailPlaced &&
          tailOccurencies > 5 &&
          (this.sutureInTissueOccurencies > 0 ||
            this.needleInTissueOccurencies > 0);

        if (isTailPlacedAndFixed) {
          this.renderService.addStatic({
            id: 'tailConfirmed',
            overlay: {
              type: 'image',
              fillColor: OverlayColor.green,
              transparency: 0.3,
              image: 'overlays/tail-stitch-confirmed.svg',
              x: openWound.min.x + 30,
              y: openWound.min.y - 40,
            },
            timeout: 2000,
          });
        } else {
          this.messageService.showDetectionMessage({
            text: 'Take the first pass in a direction away from the incision',
            identifier: 'takeFirstPass',
          });
        }
        resolve(isTailPlacedAndFixed);
      } catch (error) {
        reject(error);
      }
    });
  }

  lockStitch(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const stitches = this.detectionService.getAllBoxInstances(
          'stitch',
          this.cycleWorkerId,
        );
        const [needleInTissue, tail, toolRight] =
          this.detectionService.getBoxes(
            ['needle_in', 'tail', 'toolRight'],
            this.cycleWorkerId,
          );

        const [sutureInTissue] = this.detectionService.getBoxes(
          ['sutureInTissue'],
          this.piercingBackStitchWorkerId,
        );

        this.messageService.showDetectionMessage({
          text: 'Take a pass in the intact tissue perpendicular to the initial pass to lock the stitch',
          identifier: 'lockStitch',
        });

        if (tail) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/stitch-to-left.svg',
              x: tail.center.x + 30,
              y: tail.center.y - 60,
            },
          });
        }

        const isOneStitchFound = stitches[0]
          ? this.detectionService.isDetectionConsistentInHistory(
              'stitch',
              this.cycleWorkerId,
              4,
              1,
            ) &&
            calculateBoxIntersection(stitches[0], sutureInTissue) == 0 &&
            calculateBoxIntersection(stitches[0], toolRight) == 0
          : null;

        const isStitchLocked =
          !needleInTissue &&
          (isOneStitchFound || this.isLowLockingStitchFound());

        if (isStitchLocked) {
          this.renderService.addStatic({
            id: 'incisionConfirmed',
            overlay: {
              type: 'image',
              fillColor: OverlayColor.green,
              transparency: 0.3,
              image: 'overlays/confirmed.svg',
              x: stitches[0].center.x,
              y: stitches[0].center.y,
            },
            timeout: 2000,
          });
        }
        resolve(isStitchLocked);
      } catch (error) {
        reject(error);
      }
    });
  }

  closeWound(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const stitches = this.detectionService.getAllBoxInstances(
          'stitch',
          this.cycleWorkerId,
        );

        const [needleInTissue] = this.detectionService.getBoxes(
          ['needle_in'],
          this.cycleWorkerId,
        );

        const openWound = this.findHighConfidenceWoundInTensioner();

        if (openWound) {
          this.lastDetectedOpenWound = openWound;
        }

        const openWoundAbsence =
          this.detectionService.getNumberOfBoxEmptys(
            'open_wound',
            this.cycleWorkerId,
            5,
          ) === 5;

        if (!needleInTissue && openWound) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/stitch-to-left.svg',
              x: openWound.center.x,
              y: openWound.min.y + 10,
            },
          });
        }

        const woundIsClosed =
          this.isOneReverseStitchAndSmallWound() ||
          (stitches.length > MIN_NUMBER_OF_STITCHES &&
            openWoundAbsence &&
            this.isOpenWoundSmallEnough(this.lastDetectedOpenWound));

        if (openWound) {
          this.positionReverseStitching = {
            x: openWound.center.x,
            y: openWound.min.y,
          };
        }

        resolve(woundIsClosed);
      } catch (error) {
        reject(error);
      }
    });
  }

  reverseStitches(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.messageService.showDetectionMessage({
          text: 'Take two passes in the reverse direction across the incision',
          identifier: 'completeClosure',
        });

        const backStitches = this.detectionService.getAllBoxInstances(
          'backStitch',
          this.piercingBackStitchWorkerId,
        );

        const needleInTissueAbsent =
          this.detectionService.getNumberOfBoxEmptys(
            'needleInTissue',
            this.cycleWorkerId,
            2,
          ) === 2;

        const isFirstReverseStitchPlaced =
          this.detectionService.isDetectionConsistentInHistory(
            'backStitch',
            this.piercingBackStitchWorkerId,
            2,
            1,
          );
        const isTwoReverseStitchesPlaced =
          this.detectionService.isDetectionConsistentInHistory(
            'backStitch',
            this.piercingBackStitchWorkerId,
            3,
            2,
          );
        const isReverseStitchesDone =
          isTwoReverseStitchesPlaced && needleInTissueAbsent;

        if (
          isFirstReverseStitchPlaced &&
          this.isToShowFirstReverseStitchResult
        ) {
          this.positionReverseStitching = {
            x: this.positionReverseStitching.x,
            y: backStitches[0].center.y - 30,
          };
          this.isToShowFirstReverseStitchResult = false;
          this.renderService.addStatic({
            id: 'firstReverseStitchDone',
            overlay: {
              type: 'image',
              image: 'overlays/confirmed.svg',
              x: backStitches[0]?.center.x,
              y: backStitches[0]?.center.y,
            },
            timeout: 2000,
          });
        }

        if (isReverseStitchesDone) {
          this.renderService.addStatic({
            id: 'reverseStitchesDone',
            overlay: {
              type: 'image',
              image: 'overlays/confirmed.svg',
              x: backStitches[1]?.center.x,
              y: backStitches[1]?.center.y,
            },
            timeout: 2000,
          });
        }

        resolve(isReverseStitchesDone);
      } catch (error) {
        reject(error);
      }
    });
  }

  isLowLockingStitchFound(): boolean {
    const [toolRight] = this.detectionService.getBoxes(
      ['toolRight'],
      this.cycleWorkerId,
    );
    const openWound = this.findHighConfidenceWoundInTensioner();

    if (!openWound || !toolRight) return false;

    const historyStitches =
      this.detectionService.boxesHistory
        .get(this.cycleWorkerId)
        ?.get('stitch') ?? [];
    const recentStitchesHistory = historyStitches.slice(0, 3);

    return recentStitchesHistory.every((stitches) => {
      if (!stitches || stitches.length === 0) return false;

      return stitches.some((stitch) => {
        const distance = calculateDistance(
          {
            x: stitch.max.x,
            y: stitch.max.y,
          },
          {
            x: openWound.center.x,
            y: openWound.min.y,
          },
        );

        return (
          distance <= 80 &&
          calculateBoxIntersection(stitch, toolRight) === 0 &&
          stitch.min.y < openWound.min.y
        );
      });
    });
  }

  isOneReverseStitchAndSmallWound() {
    const backStitches = this.detectionService.getAllBoxInstances(
      'backStitch',
      this.piercingBackStitchWorkerId,
    );

    const openWounds = this.detectionService.getAllBoxInstances(
      'open_wound',
      this.cycleWorkerId,
    );

    const isFirstReverseStitchPlaced =
      this.detectionService.isDetectionConsistentInHistory(
        'backStitch',
        this.piercingBackStitchWorkerId,
        2,
        1,
      );

    const isSmallWoundFoundAfterClosure =
      openWounds[0] && backStitches[0]
        ? openWounds.length == 1 &&
          this.isOpenWoundSmallEnough(openWounds[0]) &&
          openWounds[0].center.y > backStitches[0].center.y
        : null;

    return isFirstReverseStitchPlaced && isSmallWoundFoundAfterClosure;
  }

  isOpenWoundSmallEnough(openWound: BoxDetection) {
    return this.originalWound && openWound
      ? openWound.max.y - openWound.min.y <
          0.3 * (this.originalWound.max.y - this.originalWound.min.y)
      : null;
  }

  cutSuture(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        resolve(false);
      } catch (error) {
        reject(error);
      }
    });
  }

  findHighConfidenceWoundInTensioner() {
    const openWounds = this.detectionService.getAllBoxInstances(
      'open_wound',
      this.cycleWorkerId,
    );

    const tensioner = this.detectionService.getBox(
      'tensioner',
      this.piercingBackStitchWorkerId,
    );

    return (
      openWounds.find((wound) =>
        wound && tensioner
          ? isBoxInBox({
              detection: wound,
              target: tensioner,
              confidence: GLOBAL_CONFIDENCE,
            })
          : null,
      ) || null
    );
  }

  sortVertically(detections: BoxDetection[]) {
    detections.sort((a, b) => b.min.y - a.min.y);
    return detections;
  }

  toolTracking() {
    this.stratafixMetricsService.trackLeftTool(
      this.detectionService.getBox('toolLeft', this.cycleWorkerId),
    );
    this.stratafixMetricsService.trackRightTool(
      this.detectionService.getBox('toolRight', this.cycleWorkerId),
    );
  }
}
